import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import NotificationImportantIcon from "@material-ui/icons/NotificationImportant";
import PersonIcon from "@material-ui/icons/Person";
import LiveTvIcon from "@material-ui/icons/LiveTv";
import EventIcon from "@material-ui/icons/Event";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import ArchiveIcon from "@material-ui/icons/Archive";

import { spliceTag } from "../../../utils/objectMethods/tagOperations";
import tagColors from "../../../tagColors.json";
import {
  getSearchedTicketsSum,
  wsCallTicketUpdate,
  wsCallTicketNewTicket,
  getAvailableTags,
  setColorMap,
  getSearchedTickets,
  websocketErrorReload,
} from "../../../actions/projectmail";
import { initialSearchFilter } from "../../../reducers/projectmail";
import { EVENTMGT_WEBSOCKET } from "../../../constants/constants";
import { TICKET_UPDATE, NEW_TICKET } from "../../../constants/websocketCalls";

import Websocket from "../../../lib/Websocket/Websocket";

import NavigationItem from "./NavigationItem";
import KonamiCode from "../../../utils/KonamiCode";

KonamiCode.setCode([
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "KeyB",
  "KeyA",
]);
KonamiCode.setExecudeOnSuccess(() => {
  const canvas = document.createElement("canvas");
  canvas.width = 48;
  canvas.height = 48;
  const context = canvas.getContext("2d");
  context.font = 48 + "px sans-serif";
  context.textBaseline = "middle";
  context.textAlign = "center";
  context.clearRect(0, 0, canvas.width, canvas.height);

  context.fillText("🚁", 24, 24);
  const imgDataURL = canvas.toDataURL();
  document.body.style.cursor = "url(" + imgDataURL + ") 24 0, auto";
});
document.addEventListener("keydown", event => KonamiCode.nextKey(event.code));

export const DrawerProjectMailContentComponent = ({
  getSearchedTicketsSumFn,
  translate,
  token,
  wsCallTicketUpdateFn,
  wsCallTicketNewTicketFn,
  tags,
  setColorMapFn,
  colorMap,
  getAvailableTagsFn,
  getTicketsFn,
  websocketErrorReloadFn,
}) => {
  const [ws, setWs] = useState(undefined);

  useEffect(() => {
    const request = async () => {
      if (tags.length > 0 && Object.keys(colorMap).length === 0) {
        let obj = {};
        for (let i = 0; i < tags.length; i += 1) {
          obj = {
            ...obj,
            [spliceTag(tags[i])]: tagColors[tags[i]],
          };
        }
        setColorMapFn(obj);
      }
      if (tags.length <= 0) await getAvailableTagsFn();
    };
    request();
  }, [colorMap, setColorMapFn, getAvailableTagsFn, tags]);

  useEffect(() => {
    if (ws === undefined) {
      const wss = Websocket.getInstance();
      wss.init(`${EVENTMGT_WEBSOCKET}/event-zammad/v1/ticket_ws/`, token.access_token);
      setWs(wss);
      wss.addWebsocketListeners(TICKET_UPDATE, wsCallTicketUpdateFn);
      wss.addWebsocketListeners(NEW_TICKET, wsCallTicketNewTicketFn);
      wss.addWebsocketReconnectListeners(websocketErrorReloadFn);
      const request = async () => {
        await getSearchedTicketsSumFn();
        await getTicketsFn(initialSearchFilter, "webcast");
        await getTicketsFn(initialSearchFilter, "event");
        await getTicketsFn(initialSearchFilter, "urgent");
        await getTicketsFn(initialSearchFilter, "own");
      };
      request();
    }
  }, [
    ws,
    setWs,
    wsCallTicketUpdateFn,
    wsCallTicketNewTicketFn,
    token,
    getSearchedTicketsSumFn,
    getTicketsFn,
    websocketErrorReloadFn,
  ]);

  return (
    <div>
      <NavigationItem type="event" title={translate.event_tickets} icon={<EventIcon />} />
      <NavigationItem type="webcast" title={translate.webcast_tickets} icon={<LiveTvIcon />} />
      <NavigationItem type="urgent" title={translate.urgent_tickets} icon={<NotificationImportantIcon />} />
      <NavigationItem type="own" title={translate.own_tickets} icon={<PersonIcon />} />
      <NavigationItem type="closed" title={translate.closed_tickets} icon={<HighlightOffIcon />} />
      <NavigationItem type="ticketHistory" title={translate.latest_tickets} icon={<ArchiveIcon />} />
    </div>
  );
};

DrawerProjectMailContentComponent.propTypes = {
  translate: PropTypes.object.isRequired,
  getSearchedTicketsSumFn: PropTypes.func.isRequired,
  token: PropTypes.object.isRequired,
  wsCallTicketUpdateFn: PropTypes.func.isRequired,
  wsCallTicketNewTicketFn: PropTypes.func.isRequired,
  tags: PropTypes.array.isRequired,
  setColorMapFn: PropTypes.func.isRequired,
  colorMap: PropTypes.object.isRequired,
  getAvailableTagsFn: PropTypes.func.isRequired,
  getTicketsFn: PropTypes.func.isRequired,
  websocketErrorReloadFn: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  translate: state.ui.translate,
  token: state.oauth.authData,
  tags: state.projectmail.tags,
  colorMap: state.projectmail.colorMap,
});

const mapDispatchToProps = {
  getAvailableTagsFn: () => async dispatch => {
    await dispatch(getAvailableTags());
  },
  getSearchedTicketsSumFn: () => async dispatch => {
    await dispatch(getSearchedTicketsSum());
  },
  setColorMapFn: colorMap => dispatch => dispatch(setColorMap(colorMap)),
  getTicketsFn: (filter, type) => async dispatch => {
    await dispatch(getSearchedTickets(filter, type));
  },
  wsCallTicketUpdateFn: ticketObject => (dispatch, getState) =>
    dispatch(wsCallTicketUpdate(ticketObject, getState().oauth.username)),
  wsCallTicketNewTicketFn: ticketObject => (dispatch, getState) => {
    dispatch(wsCallTicketNewTicket(ticketObject, getState().oauth.username));
  },
  websocketErrorReloadFn: () => dispatch => {
    dispatch(websocketErrorReload());
  },
};

const DrawerProjectMailContent = connect(
  mapStateToProps,
  mapDispatchToProps
)(DrawerProjectMailContentComponent);

export default DrawerProjectMailContent;
